import type { Maybe } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { confirmDialog, guardDialog } from '@adornis/dialog/dialog-functions.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import type { Booking } from '../../bookings/entities/Booking.js';
import { ClassBooking } from '../../bookings/entities/ClassBooking.js';
import { useMartinusABAC } from '../../permissions/client/useMartinusAbac.js';
import { Permission, Role } from '../../permissions/roles.js';
import { UserDetailsActions } from '../details/UserDetailsPolicy.js';
import { Person } from '../entities/person.js';
import type { UserOnUserPayload } from '../UserPolicy.js';
import { SpenderSorgeberechtigteAPI } from './SpenderSorgeberechtigteAPI.js';
import type { InviteType } from './invitationOptions.js';

const loadingSkeleton = html`
  <div class="card p-4 flex flex-row items-center gap-4">
    <div class="skeleton h-12 flex flex-grow"></div>
    <div class="skeleton h-12 flex flex-grow"></div>
    <div class="skeleton h-12 flex flex-grow"></div>
    <div class="skeleton h-12 flex flex-grow"></div>
  </div>
`;

const ChildrenCard = stateful(
  (props: {
    person: Person;
    child: NarrowedEntity<
      Person,
      { _id: 1; debitorennummer: 1; profilDaten: { stammdaten: { vorname: 1; nachname: 1 } } }
    >;
    activeBooking: Maybe<
      NarrowedEntity<
        ClassBooking | Booking,
        { groupIDs: 1; groups: { name: 1 }; grade: 1; classIdentifier: 1; startDate: 1; endDate: 1 }
      >
    >;
    type: 'Ihre sorgeberechtigten Kinder' | 'Ihre Spenden-Kinder';
  }) => {
    const user = useObservable(() => currentUser<Person>(), []);

    const { can: canRemove } = useMartinusABAC<UserOnUserPayload>({
      action: UserDetailsActions.REMOVE_CHILDREN_FROM_SBS_TEMP,
      viewedUser: props.person,
    });

    // as sorgeberechtigte and persons are allowed to view profiles, we need to check if the user has the permission to view persons in general. This is not the case for sorgeberechtigte as they can only see themselfs and get basic information of the children
    const canViewOtherPersons = user?.hasPermission(Permission.VIEW_PERSONS);
    return html`
      <a
        class="card bg-success/50 p-4 gap-4 w-max ${canViewOtherPersons ? 'cursor-pointer' : ''}"
        href=${canViewOtherPersons ? `/personen/${props.child._id}` : nothing}
      >
        <div class="flex flex-row gap-4 items-center">
          <p class="font-bold">
            ${props.child.profilDaten?.stammdaten?.vorname} ${props.child.profilDaten?.stammdaten?.nachname}
          </p>
          <p class="text-sm text-base-content/70">(${props.child.debitorennummer})</p>
        </div>

        ${
          props.activeBooking && props.activeBooking instanceof ClassBooking
            ? html`
                <p>
                  <b> ${props.activeBooking.grade} - ${props.activeBooking.classIdentifier} </b>
                  ${props.activeBooking.groups?.map(group => group.name).join(', ')}
                </p>
              `
            : html` <p>Keine aktive Buchung</p> `
        }
        ${
          canRemove
            ? html`
                <button
                  class="btn btn-ghost btn-error btn-xs btn-outline"
                  @click=${async (e: Event) => {
                    e.preventDefault();
                    e.stopPropagation();

                    if (
                      !(await confirmDialog(
                        'Sind Sie sicher, dass Sie diese Verknüpfung löschen möchten? Die Person bleibt erhalten.',
                        { highlightDecline: true, acceptText: 'Löschen', declineText: 'Abbrechen' },
                      ))
                    ) {
                      return;
                    }

                    const inviteType: InviteType =
                      props.type === 'Ihre Spenden-Kinder' ? 'Spender' : 'Sorgeberechtigter';
                    await guardDialog(
                      async () => {
                        await SpenderSorgeberechtigteAPI.remove(inviteType, props.child._id, props.person._id)();
                      },
                      { success: () => XSnackbar.show('Verknüpfung erfolgreich gelöscht') },
                    );
                  }}
                >
                  <span class="icon-[material-symbols--delete] text-error"></span>
                </button>
              `
            : nothing
        }
      </a>
    `;
  },
);

const ChildrenList = stateful(
  (props: {
    person: Person;
    childIDs: Maybe<string[]>;
    type: 'Ihre sorgeberechtigten Kinder' | 'Ihre Spenden-Kinder';
  }) => {
    const { value: children, loading: childrenLoading } = useQuery(
      () =>
        subscribeByID(
          Person,
          props.childIDs,
        )({
          _id: 1,
          debitorennummer: 1,
          profilDaten: { stammdaten: { vorname: 1, nachname: 1 } },
          activeBuchungen: { groupIDs: 1, groups: { name: 1 }, grade: 1, classIdentifier: 1, startDate: 1, endDate: 1 },
        }),
      [props.childIDs?.join(',')],
    );

    if (childrenLoading) return loadingSkeleton;

    return html`
      <div class="card p-4 flex flex-col gap-4">
        <h3 class="font-semibold md:text-lg">${props.type}</h3>
        <div class="flex flex-row gap-4 items-center">
          ${
            children?.length
              ? repeat(children, child =>
                  ChildrenCard({
                    type: props.type,
                    person: props.person,
                    child,
                    activeBooking: child.activeBuchungen?.find(b => b._class === ClassBooking._class),
                  }),
                )
              : html` <p>Es sind keine Kinder hinterlegt.</p> `
          }
        </div>
      </div>
    `;
  },
);

export const SgSorgeberechtigung = stateful((props: { personID: Maybe<string> }) => {
  const { value: person, loading: personLoading } = useQuery(
    () =>
      subscribeByID(
        Person,
        props.personID,
      )({ debitorennummer: 1, roles: { name: 1, contexts: 1 }, sorgeberechtigteKinderIDs: 1, spendenKinderIDs: 1 }),
    [props.personID],
  );

  if (personLoading) return loadingSkeleton;

  return html`
    <div class="m-4 flex flex-col gap-4">
      <!-- kept next to the relations card until everybody moved over -->
      <div role="alert" class="alert alert-warning alert-soft">
        <span class="icon-[material-symbols--warning-outline-rounded] size-6 shrink-0"></span>
        <span>
          Kinder, Sorgeberechtigte und Spender finden Sie jetzt im Profil unter <b>Beziehungen</b>. Diese Ansicht wird
          bald entfernt.
          <a class="link" href=${`/personen/${props.personID}/profil`}>Zu den Beziehungen</a>
        </span>
      </div>
      ${
        person?.roles.some(
          r =>
            r.name === Role.SORGEBERECHTIGTER ||
            r.name === Role.OLD_SORGEBERECHTIGTER ||
            r.name === Role.SUS_BEWERBER_SB,
        )
          ? ChildrenList({
              person: person as Person,
              childIDs: person.sorgeberechtigteKinderIDs,
              type: 'Ihre sorgeberechtigten Kinder',
            })
          : nothing
      }
      ${
        person?.roles.some(r => r.name === Role.SPENDER || r.name === Role.OLD_SPENDER)
          ? ChildrenList({ person: person as Person, childIDs: person.spendenKinderIDs, type: 'Ihre Spenden-Kinder' })
          : nothing
      }
    </div>
  `;
});
